import { ErrorBoundary } from './ErrorBoundary';
import { LogoDestination } from './assets/Logo/Logo';
import { FAVICON_COLOR_OVERRIDE } from './config/Environment';
import { setFaviconColor } from './favicon';
import { ANALYTICS_PATH_PREFIX } from './pages/analytics/reporting/navigation-store/quicksightDashboardPathMap';
import { NavinaLoginStep } from './pages/auth/steps/NavinaLoginStepEnum';
import { SummaryLandingPage } from './pages/summary/SummaryLandingPage.loader';
import { useFeatureFlag } from './pages/summary/behaviours/useFeatureFlag';
import { GuestOnlyRoute } from './react-router/auth/GuestOnlyRoute';
import { ProtectedRoute } from './react-router/auth/ProtectedRoute';
import { AuthLayout } from './react-router/layouts/AuthLayout';
import { BaseLayout, type LayoutHeaderProps } from './react-router/layouts/BaseLayout';
import { ForeverSpinnerLayout } from './react-router/layouts/ForeverSpinnerLayout';
import { ResponsivePaddingLayoutContainer } from './react-router/layouts/ResponsivePaddingLayout';
import { RootLayout } from './react-router/layouts/RootLayout';
import { SpinnerLayout } from './react-router/layouts/SpinnerLayout';
import { withLayout } from './react-router/layouts/withLayout';
import { PATIENT_PORTRAIT_ROUTES } from './react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes';
import {
	EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX,
	EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX,
} from './react-router/routes/PatientPortraitRoutes/PatientPortraitRoutes.utils';
import { LAUNCH_DARKLY_PAGE_NAMES, withLaunchDarklyPage } from './react-router/withLaunchDarkly';
import { slownessMeasure } from './utilities/performances/SlownessTask';
import { getOrCreateWebPerformanceModel } from './utilities/performances/WebPerformanceModel';
import { UnifiedFileManagerPagePath } from './utils/consts';
import 'normalize.css';
import { lazy, useEffect, useMemo, type ComponentProps } from 'react';
import { Navigate, createBrowserRouter, type RouteObject, RouterProvider } from 'react-router';

const {
	appointmentBasedSummary: APPOINTMENT_BASED_SUMMARY_PAGE_NAME,
	schedulingOverview: SCHEDULING_OVERVIEW_PAGE_NAME,
	patientLookup: PATIENT_LOOKUP_PAGE_NAME,
	analyticsDashboard: ANALYTICS_DASHBOARD_PAGE_NAME,
	adminTool: ADMIN_TOOL_PAGE_NAME,
	fileManager: FILE_MANAGER_PAGE_NAME,
	fileManagerUnified: FILE_MANAGER_UNIFIED_PAGE_NAME,
	documentViewer: DOCUMENT_VIEWER_PAGE_NAME,
} = LAUNCH_DARKLY_PAGE_NAMES;

const NotFound = lazy(() =>
	import('./components/ErrorPage/NotFound/NotFound').then((module) => ({
		default: module.NotFound,
	})),
);

const DocToDocumentRedirect = lazy(() =>
	import('./pages/summary/DocViewer/DocumentViewerFullPage').then((module) => ({
		default: module.DocToDocumentRedirect,
	})),
);

const ExternalRedirectPage = lazy(() =>
	import('./pages/ExternalRedirectPage/ExternalRedirectPage').then((module) => ({
		default: module.ExternalRedirectPage,
	})),
);

const AddTemplate = lazy(() =>
	import('./pages/quality-files-ingestion-tool/AddTemplate').then((module) => ({
		default: module.AddTemplate,
	})),
);

const PatientLookupPage = lazy(() =>
	import('./pages/PatientLookupPage/PatientLookupPage').then((module) => ({
		default: module.PatientLookupPage,
	})),
);

const SummaryNotFoundPage = lazy(() =>
	import('./pages/summary/SummaryNotFoundPage').then((module) => ({
		default: module.SummaryNotFoundPage,
	})),
);

const EpicLogin = lazy(() => import('./pages/epic/login'));

const VeradigmLogin = lazy(() =>
	import('./pages/veradigm/login').then((module) => ({
		default: module.VeradigmLogin,
	})),
);

const Logout = lazy(() => import('./pages/auth/Logout'));

const LoginForm = lazy(() => import('./pages/auth/LoginForm'));

const DocumentViewerFullPage = lazy(() =>
	import('./pages/summary/DocViewer/DocumentViewerFullPage').then((module) => ({
		default: module.DocumentViewerFullPage,
	})),
);

const AnalyticsPage = lazy(() =>
	import('./pages/analytics/AnalyticsPage').then((module) => ({
		default: module.AnalyticsPage,
	})),
);

const AdminToolPage = lazy(() =>
	import('./pages/admin-tool/AdminToolPage').then((module) => ({
		default: module.AdminToolPage,
	})),
);

const BulkUserInsertPage = lazy(() =>
	import('./pages/admin-tool/BulkUserInsertPage').then((module) => ({
		default: module.BulkUserInsertPage,
	})),
);

const AntdDemo = lazy(() => import('./pages/antd-demo/AntdDemo'));

const FileIngestionToolPage = lazy(() =>
	import('./pages/quality-files-ingestion-tool/PageContainer').then((module) => ({
		default: module.FileIngestionToolPage,
	})),
);

const FITTemplatesPage = lazy(() =>
	import('./pages/quality-fit-templates/PageContainer').then((module) => ({
		default: module.FITTemplatesPage,
	})),
);

const QualityTabByNidPage = lazy(() =>
	import('./pages/quality/quality-4-tab-by-nid/PageContainer').then((module) => ({
		default: module.QualityTabByNid,
	})),
);

const AmbientNoteEditorDemoPage = lazy(() =>
	import('./pages/demo/AmbientNoteEditorDemo').then((module) => ({
		default: module.AmbientNoteEditorDemo,
	})),
);

const MobileLoginQRPage = lazy(() =>
	import('./pages/ambient/mobile-qr-page/MobileQRPage').then((module) => ({
		default: module.MobileQRPage,
	})),
);

const SchedulePage = lazy(() =>
	import('./pages/SchedulePage/SchedulePage').then((module) => ({
		default: module.SchedulePage,
	})),
);

const Quality = lazy(() =>
	import('./pages/quality/care-team/PageContainer').then((module) => ({
		default: module.PageContainer,
	})),
);

const PatientLookupHeader = lazy(() =>
	import('./pages/PatientLookupPage/PatientLookupHeader').then((module) => ({
		default: module.PatientLookupHeader,
	})),
);

const FilesManager = lazy(() =>
	import('./pages/file-manager/PageContainer').then((module) => ({
		default: module.PageContainer,
	})),
);

const FileManagerUnified = lazy(() =>
	import('./pages/FileManagerUnified/FileManagerPage').then((module) => ({
		default: module.FileManagerPage,
	})),
);

const CareTeamHeader = lazy(() =>
	import('./pages/quality/care-team/CareTeamHeader').then((module) => ({
		default: module.CareTeamHeader,
	})),
);

const AUTHORIZED_USER_ACTION_ROUTES = [
	{
		path: '/auth/changePassword',
		element: (
			<LoginForm
				{...{
					defaultStepId: NavinaLoginStep.ChangePassword,
				}}
			/>
		),
	},
	{
		...withLayout(<SpinnerLayout />, [
			{
				path: '/logout',
				element: <Logout />,
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

function getChromeExtensionStoreUrl(chromePublicExtensionId: string): string {
	return `https://chromewebstore.google.com/detail/navina-addon/${chromePublicExtensionId}`;
}

interface ChromeExtensionRoute {
	readonly path: string;
	readonly chromePublicExtensionId: string;
}

const CHROME_EXTENSIONS = [
	{
		path: '/athena',
		chromePublicExtensionId: 'plhceajggdihcnffppedpfjpflpmdakh',
	},
	{
		path: '/ecw',
		chromePublicExtensionId: 'clkcfpclllbaegehjacadbpejiocgkii',
	},
] as const satisfies ReadonlyArray<ChromeExtensionRoute>;

const CHROME_EXTENSION_STORE_REDIRECTS = CHROME_EXTENSIONS.map(
	({ path, chromePublicExtensionId }) =>
		({
			path,
			element: (
				<ExternalRedirectPage
					{...{
						url: getChromeExtensionStoreUrl(chromePublicExtensionId),
					}}
				/>
			),
		}) as const satisfies RouteObject,
);

const SCHEDULE_PAGE_ROUTES = [
	{
		...withLaunchDarklyPage(SCHEDULING_OVERVIEW_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerAppMenuSelectedPage: 'patient-portrait',
					sideHeaderComponent: PatientLookupHeader,
					headerTitle: 'Scheduling Overview',
					headerLogoLink: LogoDestination.Schedule,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/',
					element: <Navigate to='/schedule' />,
				},
				{
					path: '/schedule',
					element: <SchedulePage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const PATIENT_LOOKUP_ROUTES = [
	{
		...withLaunchDarklyPage(PATIENT_LOOKUP_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerAppMenuSelectedPage: 'patient-portrait',
					sideHeaderComponent: PatientLookupHeader,
					headerTitle: 'Patient Lookup',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/patient-lookup/:navinaPatientId',
					element: <PatientLookupPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const ANALYTICS_ROUTES = [
	{
		...withLaunchDarklyPage(ANALYTICS_DASHBOARD_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerTitle: 'Analytics',
					headerAppMenuSelectedPage: 'dashboard',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: `${ANALYTICS_PATH_PREFIX}/*`,
					element: <AnalyticsPage />,
				},
				{
					path: '/qs',
					element: <Navigate to={ANALYTICS_PATH_PREFIX} />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const ADMIN_ROUTES = [
	{
		...withLaunchDarklyPage(ADMIN_TOOL_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerTitle: 'Admin',
					headerAppMenuSelectedPage: 'admin',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/admin',
					element: (
						<ResponsivePaddingLayoutContainer>
							<AdminToolPage />
						</ResponsivePaddingLayoutContainer>
					),
				},
				{
					path: '/admin/insert-users',
					element: (
						<ResponsivePaddingLayoutContainer>
							<BulkUserInsertPage />
						</ResponsivePaddingLayoutContainer>
					),
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_WORKSPACE_ROUTE_PROPS = {
	sideHeaderComponent: CareTeamHeader,
	headerAppMenuSelectedPage: 'quality',
	headerLogoLink: LogoDestination.Nowhere,
	headerTitle: 'Quality Workspace',
} as const satisfies LayoutHeaderProps;

const QUALITY_POP_WORKSPACE_ROUTE_PROPS = {
	...QUALITY_WORKSPACE_ROUTE_PROPS,
	headerTitle: 'Pop Health Workspace',
} as const satisfies LayoutHeaderProps;

const QUALITY_WORKSPACE_ROUTES = [
	{
		...withLayout(<BaseLayout {...QUALITY_WORKSPACE_ROUTE_PROPS} />, [
			{
				element: <Quality />,
				children: [
					{
						path: '/quality-satisfaction-rate',
					},
				],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const FILES_ROUTES = [
	{
		...withLaunchDarklyPage(FILE_MANAGER_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerAppMenuSelectedPage: 'files',
					headerTitle: 'File manager',
					headerLogoLink: LogoDestination.Files,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					element: <FilesManager />,
					path: '/files',
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const FILE_MANAGER_ROUTES = [
	{
		...withLaunchDarklyPage(FILE_MANAGER_UNIFIED_PAGE_NAME),
		...withLayout(
			<BaseLayout
				{...({
					headerAppMenuSelectedPage: 'files',
					headerTitle: 'File Manager',
					headerLogoLink: LogoDestination.Nowhere,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					element: <FileManagerUnified />,
					path: UnifiedFileManagerPagePath,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_FIT_ROUTES = [
	{
		...withLayout(
			<BaseLayout
				{...({
					headerAppMenuSelectedPage: 'quality',
					headerLogoLink: LogoDestination.Nowhere,
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/quality-fit',
					element: <FileIngestionToolPage />,
				},
				{
					path: '/quality-fit/add-template',
					element: <AddTemplate />,
				},
				{
					path: '/quality-fit-templates',
					element: <FITTemplatesPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_PATIENT_BASED_ROUTES = [
	{
		...withLayout(
			<BaseLayout
				{...({
					...QUALITY_WORKSPACE_ROUTE_PROPS,
					headerTitle: 'Quality patient view',
				} as const satisfies LayoutHeaderProps)}
			/>,
			[
				{
					path: '/quality-tab',
					element: <QualityTabByNidPage />,
				},
			],
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const QUALITY_POP_WORKSPACE_ROUTES = [
	{
		...withLayout(<BaseLayout {...QUALITY_POP_WORKSPACE_ROUTE_PROPS} />, [
			{
				element: <Quality />,
				children: [
					{
						path: '/pop-health-admissions',
					},
					{
						path: '/pop-health-radv-audit',
					},
					{
						path: '/pop-health-cost',
					},
				],
			},
		]),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const SUMMARY_LANDING_PAGE_PROPS = {
	overlayPageName: null,
} as const satisfies ComponentProps<typeof SummaryLandingPage>;

const SUMMARY_ROUTES = [
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		element: <SummaryNotFoundPage hideActionButton={false} />,
		children: [
			{
				path: '/summary/guess-match-not-found',
			},
			{
				path: '/overlay/summary/guess-match-not-found',
			},
			{
				path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/summary/guess-match-not-found`,
			},
		],
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/:navinaAppointmentId',
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/:navinaAppointmentId',
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/hcc/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'hcc',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/clinical/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/portrait/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/documents/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'documents',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/alerts/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'alerts',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: '/overlay/ambient/:navinaAppointmentId',
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'ambient',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/:navinaAppointmentId`,
		element: <SummaryLandingPage {...SUMMARY_LANDING_PAGE_PROPS} />,
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/hcc/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'hcc',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/clinical/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/portrait/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'portrait',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/documents/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'documents',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/alerts/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'alerts',
				}}
			/>
		),
	},
	{
		...withLaunchDarklyPage(APPOINTMENT_BASED_SUMMARY_PAGE_NAME),
		path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/ambient/:navinaAppointmentId`,
		element: (
			<SummaryLandingPage
				{...{
					...SUMMARY_LANDING_PAGE_PROPS,
					overlayPageName: 'ambient',
				}}
			/>
		),
	},
] as const satisfies ReadonlyArray<RouteObject>;

const DOCUMENT_VIEWER_ROUTES = [
	{
		...withLaunchDarklyPage(DOCUMENT_VIEWER_PAGE_NAME),
		path: '/doc/:documentId',
		element: <DocToDocumentRedirect />,
	},
	{
		...withLaunchDarklyPage(DOCUMENT_VIEWER_PAGE_NAME),
		path: '/document/:documentId',
		element: <DocumentViewerFullPage />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

const AMBIENT_ROUTES = [
	{
		path: '/ambient-note-demo',
		element: <AmbientNoteEditorDemoPage />,
	},
	{
		path: '/mobile-login-qr',
		element: <MobileLoginQRPage />,
	},
] as const satisfies ReadonlyArray<RouteObject>;

const PAGE_NOT_FOUND_ROUTE = {
	path: '*',
	element: (
		<NotFound
			{...{
				entityName: 'Page',
				action: {
					onAction: function goHome(): void {
						globalThis.location.href = '/';
					},
					name: 'Go to Scheduling Overview',
				},
			}}
		/>
	),
} as const satisfies RouteObject;

export function App() {
	slownessMeasure.timeLog('React has started');

	const webPerformanceModel = getOrCreateWebPerformanceModel();
	useEffect(webPerformanceModel.initializeOnce, [webPerformanceModel]);

	useEffect(function applyFaviconColorOverride(): void {
		if (FAVICON_COLOR_OVERRIDE) {
			setFaviconColor({ colorOverride: FAVICON_COLOR_OVERRIDE });
		}
	}, []);

	const popHealthEnabled = useFeatureFlag('navina_user_features', false);

	const router = useMemo(
		function getRouter(): ReturnType<typeof createBrowserRouter> {
			const qualityRoutes = [
				{
					...withLayout(
						<BaseLayout
							{...({
								...QUALITY_WORKSPACE_ROUTE_PROPS,
								...(popHealthEnabled ? QUALITY_POP_WORKSPACE_ROUTE_PROPS : {}),
							} as const satisfies LayoutHeaderProps)}
						/>,
						[
							{
								element: <Quality />,
								children: [
									{
										path: '/quality',
									},
									{
										path: '/quality-workspace',
									},
									{
										path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/quality-workspace`,
									},
									{
										path: `${EMBEDDED_LEGACY_APPOINTMENT_PORTRAIT_PATH_PREFIX}/quality-workspace`,
									},
									{
										path: '/quality-archive',
									},
									{
										path: '/quality-patients',
									},
								],
							},
						],
					),
				},
			] as const satisfies ReadonlyArray<RouteObject>;

			const guestOnlyAndAnyoneRoutes = [
				{
					element: <GuestOnlyRoute />,
					children: [
						{
							...withLayout(<AuthLayout />, [
								{
									element: <LoginForm />,
									children: [
										{
											path: '/sso',
										},
										{
											path: '/sso/*',
										},
									],
								},
							]),
						},
					],
				},
				{
					// AnyoneRoute
					children: [
						{
							...withLayout(<ForeverSpinnerLayout />, [
								{
									element: <EpicLogin />,
									children: [
										{
											path: '/epic/login',
										},
										{
											path: '/epic/login/*',
										},
										{
											path: '/overlay/epic/login',
										},
										{
											path: '/overlay/epic/login/*',
										},

										{
											path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/epic/login`,
										},
										{
											path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/epic/login/*`,
										},
									],
								},
							]),
						},
						{
							...withLayout(<SpinnerLayout />, [
								{
									element: <VeradigmLogin />,
									path: '/veradigm/launch',
								},
								{
									element: <VeradigmLogin />,
									path: '/veradigm/launch/*',
								},
							]),
						},
					],
				},
				{
					element: <GuestOnlyRoute />,
					children: [
						{
							...withLayout(<SpinnerLayout />, [
								{
									element: <LoginForm />,
									children: [
										{
											path: '/login',
										},
										{
											path: '/login/*',
										},
										{
											path: '/overlay/login',
										},
										{
											path: '/overlay/login/*',
										},
										{
											path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/login`,
										},
										{
											path: `${EMBEDDED_PATIENT_PORTRAIT_PATH_PREFIX}/login/*`,
										},
									],
								},
							]),
						},
					],
				},
			] as const satisfies ReadonlyArray<RouteObject>;

			const protectedRoutes = [
				{
					element: <ProtectedRoute />,
					children: [
						...AUTHORIZED_USER_ACTION_ROUTES,
						...SCHEDULE_PAGE_ROUTES,
						...PATIENT_LOOKUP_ROUTES,
						...PATIENT_PORTRAIT_ROUTES,
						...ANALYTICS_ROUTES,
						...ADMIN_ROUTES,
						{
							path: '/antd-demo',
							element: <AntdDemo />,
						},
						...qualityRoutes,
						...QUALITY_WORKSPACE_ROUTES,
						...FILES_ROUTES,
						...FILE_MANAGER_ROUTES,
						...QUALITY_FIT_ROUTES,
						...QUALITY_PATIENT_BASED_ROUTES,
						...QUALITY_POP_WORKSPACE_ROUTES,
						...DOCUMENT_VIEWER_ROUTES,
						...AMBIENT_ROUTES,
					],
				},
			] as const satisfies ReadonlyArray<RouteObject>;

			// This could be removed once ProtectedRoute's `isSummaryView` prop is gone
			const summaryProtectedRoutes = [
				{
					element: <ProtectedRoute isSummaryView={true} />,
					children: [...SUMMARY_ROUTES],
				},
			] as const satisfies ReadonlyArray<RouteObject>;

			const anyoneRoutes = [
				{
					children: [...CHROME_EXTENSION_STORE_REDIRECTS],
				},
			] as const satisfies ReadonlyArray<RouteObject>;

			return createBrowserRouter([
				{
					element: <RootLayout />,
					errorElement: <ErrorBoundary />,
					children: [
						...guestOnlyAndAnyoneRoutes,
						...anyoneRoutes,
						...protectedRoutes,
						...summaryProtectedRoutes,
						PAGE_NOT_FOUND_ROUTE,
					],
				},
			]);
		},
		[popHealthEnabled],
	);

	return (
		<RouterProvider
			{...{
				router,
			}}
		/>
	);
}
